import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import {
  GraduationCap, Send, Loader2, BookOpen, Target, Globe,
  Sparkles, ArrowRight, Save, MessageSquare, RefreshCw, User, Bot, TrendingUp, Coins
} from 'lucide-react';
import { AI_PERSONAS } from '../constants/aiPersonas';

const AcademicAdvisor = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('welcome'); // welcome, questionnaire, chat
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({
    interests: [],
    goals: '',
    target_country: '',
    custom_country: '',
    education_level: ''
  });
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Check if user is logged in
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startSession = async () => {
    // Check if user is logged in - redirect to pricing if not
    if (!token) {
      toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
      navigate('/pricing');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/academic-advisor/start-session');
      setSessionId(response.data.session_id);
      setQuestionnaire(response.data.initial_questions);
      setMessages([{
        role: 'assistant',
        content: response.data.welcome_message
      }]);
      setStage('questionnaire');
    } catch (error) {
      // If 402 (payment required), redirect to pricing
      if (error.response?.status === 402) {
        toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
        navigate('/pricing');
      } else {
        toast({ title: "Error", description: "Failed to start session", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInterestToggle = (interest) => {
    setAnswers(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const submitQuestionnaire = async () => {
    // Use custom country if provided, otherwise use selected country
    const targetCountry = answers.custom_country.trim() || answers.target_country;
    
    if (answers.interests.length === 0 || !answers.goals || !targetCountry || !answers.education_level) {
      toast({ title: "Incomplete", description: "Please answer all questions", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/academic-advisor/submit-questionnaire', {
        ...answers,
        target_country: targetCountry,
        session_id: sessionId,
        user_id: user?._id || null
      });
      
      setQuestionnaireData(response.data.questionnaire_data);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
      setStage('chat');
    } catch (error) {
      if (error.response?.status === 402) {
        toast({ title: "Credits Required", description: "Please purchase credits to use Faith.", variant: "default" });
        navigate('/pricing');
        return;
      }
      toast({ title: "Error", description: "Failed to process your answers", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    try {
      setLoading(true);
      const response = await api.post('/academic-advisor/chat', {
        content: userMessage,
        session_id: sessionId,
        user_id: user?._id
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      if (error.response?.status === 402) {
        toast({ title: "Credits Required", description: "Please purchase credits to continue chatting with Faith.", variant: "default" });
        navigate('/pricing');
        return;
      }
      toast({ title: "Error", description: "Failed to get response", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const requestCollegeRecommendations = async () => {
    if (!questionnaireData) return;
    
    const message = `Based on my interests (${questionnaireData.interests.join(', ')}), goals (${questionnaireData.goals}), and target country (${questionnaireData.target_country}), can you recommend some specific colleges/universities that would be a good fit for me?`;
    
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    
    try {
      setLoading(true);
      const response = await api.post('/academic-advisor/recommend-colleges', {
        content: message,
        session_id: sessionId
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      toast({ title: "Error", description: "Failed to get recommendations", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const saveSession = async () => {
    if (!user) {
      toast({ title: "Login Required", description: "Please log in to save your session", variant: "destructive" });
      return;
    }

    try {
      await api.post('/academic-advisor/save-session', {
        session_id: sessionId,
        user_id: user._id
      });
      toast({ title: "Saved!", description: "Your session has been saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save session", variant: "destructive" });
    }
  };

  const startNewSession = () => {
    setStage('welcome');
    setSessionId(null);
    setMessages([]);
    setAnswers({ interests: [], goals: '', target_country: '', custom_country: '', education_level: '' });
    setQuestionnaireData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 overflow-hidden border-4 border-white/30 shadow-lg">
            <img 
              src={AI_PERSONAS.FAITH.avatar} 
              alt="Faith - Academic Advisor"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Meet Faith - Your Academic Advisor</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Get personalized guidance on majors, career paths, and academic planning based on your interests and goals
          </p>
          <div className="flex justify-center gap-4 mt-6">
            <Badge className="bg-white/20 text-white border-0">
              <Coins className="h-3 w-3 mr-1" /> 2 Credits Per Use
            </Badge>
            <Badge className="bg-white/20 text-white border-0">
              <Sparkles className="h-3 w-3 mr-1" /> AI Powered
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Stage */}
        {stage === 'welcome' && (
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="max-w-xl mx-auto">
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <BookOpen className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                    <p className="text-sm font-medium">Major Selection</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <Target className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-medium">Career Guidance</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-xl">
                    <Globe className="h-8 w-8 text-pink-600 mx-auto mb-2" />
                    <p className="text-sm font-medium">Global Insights</p>
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold mb-4">Ready to Find Your Path?</h2>
                <p className="text-gray-600 mb-8">
                  Answer a few questions about your interests and goals, and I'll provide personalized 
                  major recommendations and career guidance tailored to your target work country.
                </p>
                
                <Button 
                  onClick={startSession} 
                  disabled={loading}
                  size="lg"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-8"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-5 w-5 mr-2" />
                  )}
                  Start My Session
                </Button>
                
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-500 mb-3">Already know what you want to study?</p>
                  <Link to="/career-simulator">
                    <Button variant="outline" className="border-pink-300 text-pink-600 hover:bg-pink-50">
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Talk to Ashish - Career Simulator
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questionnaire Stage */}
        {stage === 'questionnaire' && questionnaire && (
          <div className="space-y-6">
            {/* Welcome Message */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5" />
                  </div>
                  <p className="text-lg">{messages[0]?.content}</p>
                </div>
              </CardContent>
            </Card>

            {/* Questions */}
            {questionnaire.map((q, index) => (
              <Card key={q.id} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    {q.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {q.type === 'multi_select' ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {q.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => handleInterestToggle(option)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            answers.interests.includes(option)
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <span className="text-sm font-medium">{option}</span>
                        </button>
                      ))}
                    </div>
                  ) : q.id === 'target_country' ? (
                    <div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {q.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => {
                              setAnswers(prev => ({ ...prev, target_country: option, custom_country: '' }));
                            }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              answers.target_country === option && !answers.custom_country
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <span className="text-sm font-medium">{option}</span>
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <Input
                          placeholder="Or type any other country..."
                          value={answers.custom_country}
                          onChange={(e) => {
                            setAnswers(prev => ({ 
                              ...prev, 
                              custom_country: e.target.value,
                              target_country: e.target.value ? '' : prev.target_country 
                            }));
                          }}
                          className="border-gray-200"
                        />
                        {answers.custom_country && (
                          <p className="text-xs text-purple-600 mt-2">
                            Using custom country: {answers.custom_country}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {q.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => setAnswers(prev => ({ ...prev, [q.id]: option }))}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            answers[q.id] === option
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <span className="text-sm font-medium">{option}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-center">
              <Button 
                onClick={submitQuestionnaire}
                disabled={loading}
                size="lg"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-8"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-5 w-5 mr-2" />
                )}
                Get My Recommendations
              </Button>
            </div>
          </div>
        )}

        {/* Chat Stage */}
        {stage === 'chat' && (
          <div className="space-y-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={startNewSession}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  New Session
                </Button>
                {user && (
                  <Button variant="outline" size="sm" onClick={saveSession}>
                    <Save className="h-4 w-4 mr-1" />
                    Save Session
                  </Button>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={requestCollegeRecommendations}
                disabled={loading}
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              >
                <GraduationCap className="h-4 w-4 mr-1" />
                Get College Recommendations
              </Button>
            </div>

            {/* Chat Messages */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-0">
                <div className="h-[500px] overflow-y-auto p-6 space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 ${
                        msg.role === 'user' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' 
                          ? 'bg-indigo-100 text-indigo-600' 
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                      }`}>
                        {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div className={`max-w-[80%] p-4 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-indigo-600 text-white rounded-tr-sm'
                          : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                      }`}>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                      <div className="bg-gray-100 p-4 rounded-2xl rounded-tl-sm">
                        <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Ask about majors, careers, or academic planning..."
                      className="flex-1"
                      disabled={loading}
                    />
                    <Button 
                      onClick={sendMessage} 
                      disabled={loading || !inputMessage.trim()}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Ask follow-up questions about majors, career paths, or request college recommendations
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        {stage === 'welcome' && (
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">What I Can Help With</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  "Choose a major based on your interests and goals",
                  "Understand career prospects in different countries",
                  "Get college/university recommendations",
                  "Plan your academic journey",
                  "Explore different career paths",
                  "Understand job market trends"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">How It Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { step: 1, title: "Answer Questions", desc: "Tell me about your interests and goals" },
                  { step: 2, title: "Get Recommendations", desc: "Receive personalized major suggestions" },
                  { step: 3, title: "Explore Further", desc: "Ask follow-up questions and dive deeper" },
                  { step: 4, title: "Save (Optional)", desc: "Log in to save your session for later" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.title}</p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicAdvisor;
